import { createRouter, createWebHistory } from 'vue-router'
import request, { getTenantDomain } from '@/api/request'

const routes = [
  // 前台页面路由
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { title: '首页' }
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('@/views/Products.vue'),
    meta: { title: '产品中心' }
  },
  {
    path: '/products/:id',
    name: 'ProductDetail',
    component: () => import('@/views/ProductDetail.vue'),
    meta: { title: '产品详情' }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/About.vue'),
    meta: { title: '关于我们' }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/Contact.vue'),
    meta: { title: '联系我们' }
  },
  
  // 后台管理路由
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/login/Login.vue'),
    meta: { title: '管理员登录', requiresAuth: false }
  },
  {
    path: '/admin',
    component: () => import('@/views/admin/layout/AdminLayout.vue'),
    redirect: '/admin/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/dashboard/Dashboard.vue'),
        meta: { title: '仪表盘', icon: 'Odometer' }
      },
      {
        path: 'products',
        name: 'AdminProducts',
        component: () => import('@/views/admin/products/ProductList.vue'),
        meta: { title: '产品管理', icon: 'Goods' }
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('@/views/admin/categories/CategoryList.vue'),
        meta: { title: '分类管理', icon: 'Menu' }
      },
      {
        path: 'i18n',
        name: 'AdminI18n',
        component: () => import('@/views/admin/i18n/I18nManagement.vue'),
        meta: { title: '多语言管理', icon: 'ChatDotRound' }
      },
      {
        path: 'banner',
        name: 'AdminBanner',
        component: () => import('@/views/admin/banner/BannerManagement.vue'),
        meta: { title: '轮播图管理', icon: 'Picture' }
      },
      {
        path: 'customer-service',
        name: 'AdminCustomerService',
        component: () => import('@/views/admin/config/CustomerServiceManagement.vue'),
        meta: { title: '客服管理', icon: 'Service' }
      },
      {
        path: 'inquiry',
        name: 'AdminInquiry',
        component: () => import('@/views/admin/inquiry/Index.vue'),
        meta: { title: '询价管理', icon: 'Promotion' }
      },
      {
        path: 'config',
        name: 'AdminConfig',
        component: () => import('@/views/admin/config/Config.vue'),
        meta: { title: '系统配置', icon: 'Setting' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 域名跳转缓存（每个会话只检查一次）
let domainCheckDone = false

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 捕获租户ID（从URL参数保存到localStorage，供后续请求使用）
  const tenantId = to.query.tenant_id || to.query.tenantId || to.query.tenant
  if (tenantId) {
    localStorage.setItem('tenant_id', String(tenantId))
  }

  // 域名跳转检查（仅前台页面、生产环境、首次导航时执行）
  const isPublicPage = !to.path.startsWith('/admin')
  const host = window.location.hostname
  const port = window.location.port
  const isProduction = host !== 'localhost' && host !== '127.0.0.1' && !port

  if (isPublicPage && isProduction && !domainCheckDone) {
    domainCheckDone = true
    try {
      // 发起请求，后端在响应头 X-Tenant-Domain 中返回租户绑定域名
      await request.get('/configs/all')
      // 响应拦截器已捕获域名，直接读取
      const domain = getTenantDomain()
      if (domain && domain.trim() && domain.trim() !== host) {
        const targetUrl = `${window.location.protocol}//${domain.trim()}${to.fullPath}`
        console.log(`域名跳转: ${host} -> ${domain}`)
        window.location.href = targetUrl
        return // 跳转中，不继续导航
      }
    } catch (error) {
      console.error('域名跳转检查失败:', error)
    }
  }

  const token = localStorage.getItem('admin_token')
  
  // 检查当前路由或其父路由是否需要认证
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false && (record.meta.requiresAuth || record.path.startsWith('/admin')))
  
  if (requiresAuth && !token) {
    // 需要认证但没有token，跳转到登录页
    next('/admin/login')
  } else if (to.path === '/admin/login' && token) {
    // 已登录用户访问登录页，跳转到仪表盘
    next('/admin/dashboard')
  } else {
    next()
  }
})

export default router